import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import { ProductSubscriptionApiService } from '../../../../../service/seller/productSubscriptionApiService';
import { useLoader } from '../../../../../base/context/LoaderProvider';
import { ToastTypes, useMessage } from '../../../../../base/context/MessageProvider';
import { useOutsideClick } from '../../../../../base/context/OutsideClick';
// import { RiArrowDropDownLine } from 'react-icons/ri';
import './style.css';

// Set app element for react-modal accessibility
Modal.setAppElement('#root');

export default function SyncProducts({ selectedSKUs, setSelectedSKUs, setSelectAll }) {
  // DROPDOWN STATE COMMENTED OUT
  // const [showOptions, setShowOptions] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [syncedSkus, setSyncedSkus] = useState([]);
  const [unsyncedSkus, setUnsyncedSkus] = useState([]);

  const loader = useLoader();
  const message = useMessage();
  // const outsideClick = useOutsideClick();
  // const ref = useRef(null);

  const options = [
    { name: 'All Products ', value: 'all' },
    { name: 'Selected Products ', value: 'selected' },
  ];

  // DROPDOWN HANDLER COMMENTED OUT
  // const handleOptions = () => {
  //   setShowOptions(!showOptions);
  // };

  const handleSync = async type => {
    // close dropdown (no-op now, but kept for compatibility)
    // setShowOptions(false);

    if (type === 'selected' && !selectedSKUs.length) {
      return message.showToast('Select at least one product', ToastTypes.ERROR);
    }

    if (type === 'all') {
      // old behavior: sync ALL products in account
      loader.showLoading(true);
      const toastId = message.showToast(
        'Processing sync all products, please wait...',
        ToastTypes.LOADING
      );

      try {
        const res = await ProductSubscriptionApiService.syncAllProducts();
        loader.showLoading(false);

        if (res.status.code === 200) {
          message.showToast(res.status.message, ToastTypes.SUCCESS, { toastId });
          setSelectedSKUs([]);
          setSelectAll(false);
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR, { toastId });
        }
      } catch (err) {
        loader.showLoading(false);
        message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
      }

      return;
    }

    // type === 'selected':
    // 1) figure out which selected are new vs already-synced
    const { synced, unsynced } = separateSkusBySync(selectedSKUs);

    setSyncedSkus(synced);
    setUnsyncedSkus(unsynced);

    // 2) First sync new (unsynced=false aka create)
    if (unsynced.length > 0) {
      await syncProducts(unsynced, /*areSynced*/ false);
    }

    // 3) If there are already-synced ones, ask confirmation to update
    if (synced.length > 0) {
      setShowModal(true);
    } else {
      // nothing left to update, reset selection
      setSelectedSKUs([]);
      setSelectAll(false);
    }
  };

  const handleModalConfirm = async () => {
    setShowModal(false);

    if (syncedSkus.length > 0) {
      // syncedSkus is array of SKUs that were already in Shopify
      loader.showLoading(true);
      await syncProducts(syncedSkus, /*areSynced*/ true);
      loader.showLoading(false);
    }

    // Reset
    setSelectedSKUs([]);
    setSelectAll(false);
    setSyncedSkus([]);
    setUnsyncedSkus([]);
  };

  const handleModalCancel = () => {
    setShowModal(false);

    // Reset selections
    setSelectedSKUs([]);
    setSelectAll(false);
    setSyncedSkus([]);
    setUnsyncedSkus([]);
  };

  // OUTSIDE CLICK HANDLING (ONLY FOR DROPDOWN) COMMENTED OUT
  // const onClickOutside = () => {
  //   setShowOptions(false);
  // };

  // useEffect(() => {
  //   outsideClick.getComponent(ref, onClickOutside);
  // }, [onClickOutside, outsideClick]);

  const separateSkusBySync = skuList => {
    const finalSkus = [];

    skuList.forEach(item => {
      // If user checked specific variants in UI:
      if (Array.isArray(item.variantSkuIds) && item.variantSkuIds.length > 0) {
        item.variantSkuIds.forEach(vSku => {
          finalSkus.push({ sku: String(vSku), isSynced: item.isSynced });
        });
      } else {
        // User only checked parent row (means sync ALL variants / whole product)
        finalSkus.push({ sku: String(item.sku), isSynced: item.isSynced });
      }
    });

    const synced = [];
    const unsynced = [];

    finalSkus.forEach(({ sku, isSynced }) => {
      if (isSynced) synced.push(sku);
      else unsynced.push(sku);
    });

    return { synced, unsynced };
  };

  const syncProducts = async (skuArray, areSynced) => {
    if (!skuArray.length) return;

    const syncTypeText = areSynced ? 'updating' : 'creating';
    const toastId = message.showToast(
      `Processing ${syncTypeText} ${skuArray.length} product(s) on Shopify...`,
      ToastTypes.LOADING
    );

    try {
      const res = await ProductSubscriptionApiService.syncSelectedProducts(
        JSON.stringify({
          skus: skuArray,
          areSynced: areSynced,
        })
      );

      if (res.status.code === 200) {
        const actionText = areSynced ? 'updated' : 'synced';
        message.showToast(
          `${skuArray.length} product(s) ${actionText} successfully on Shopify`,
          ToastTypes.SUCCESS,
          { toastId }
        );
      } else {
        // backend surfaced error message
        message.showToast(
          res.status.message || res.error?.message || 'Sync failed',
          ToastTypes.ERROR,
          { toastId }
        );
      }
    } catch (err) {
      message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR, { toastId });
    }
  };

  return (
    <div className="relative">
      {/* BUTTON NOW DIRECTLY TRIGGERS SYNC FOR SELECTED PRODUCTS */}
      <div
        onClick={() => handleSync('selected')}
        className="filter_dropdown export_dropdown_btn"
      >
        Sync Products
        {/* DROPDOWN ICON COMMENTED OUT */}
        {/* <RiArrowDropDownLine className="export_dropdown_icon" /> */}
      </div>

      {/* DROPDOWN OPTIONS COMMENTED OUT */}
      {/*
      {showOptions ? (
        <div
          ref={ref}
          className={`${showOptions ? 'w-60 h-auto' : null
            } h-0 bg-white border absolute shadow-xl flex flex-col py-4 z-[1]`}
        >
          <p className="sync_popup_head">On Shopify</p>
          {options.map(item => {
            return (
              <p
                key={item.name}
                className="sync_popup_option"
                onClick={() => handleSync(item.value)}
              >
                {item.name}
              </p>
            );
          })}
        </div>
      ) : null}
      */}

      {/* Confirmation Modal */}
      <Modal
        isOpen={showModal}
        onRequestClose={handleModalCancel}
        contentLabel="Sync Confirmation"
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={true}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
             max-w-[500px] w-[90%] p-6 rounded-xl border-0 
             shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_10px_10px_-5px_rgba(0,0,0,0.04)] 
             bg-white"
        overlayClassName="fixed inset-0 bg-black/50 z-[9999]"
      >
        <div className="modal-content">
          <div className="modal-header mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Update Already Synced Products?
            </h3>
          </div>

          <div className="modal-body mb-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-yellow-800">
                    Warning: Product Update
                  </h4>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      You have <strong>{syncedSkus.length}</strong> products
                      that are already synced to Shopify.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-3 text-sm text-gray-600">
              <p>
                <strong>If you continue:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Product details will be updated with latest information</li>
                <li>New variants will be added (only those variants you selected)</li>
                <li>New images will be uploaded</li>
                <li>
                  <span className="text-red-600 font-medium">
                    Existing images may be replaced
                  </span>
                </li>
              </ul>

              <p className="mt-4">
                Do you want to proceed with updating these {syncedSkus.length}{' '}
                already synced products?
              </p>
            </div>

            {unsyncedSkus.length > 0 && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700">
                  <span className="font-medium">✓ Completed:</span>{' '}
                  {unsyncedSkus.length} new product(s) have been queued to sync
                  to Shopify with the variants you selected.
                </p>
              </div>
            )}
          </div>

          <div className="modal-footer flex justify-end space-x-3">
            <button
              onClick={handleModalCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              No, Skip Updates
            </button>
            <button
              onClick={handleModalConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Yes, Update Products
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
